import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useFocusable } from "@noriginmedia/norigin-spatial-navigation";
import useClock from "./hooks/useClock";
import { getNow } from "./utils/shared/time";
import { useEvents } from "./context/EventsContext";
import backArrow from "../assets/backArrow.svg";
import streamingReadinessIcon from "../assets/streamingReadiness.svg";
import MarketingButton from "./components/MarketingButton";
import { getHeaders } from "./utils/shared/headers";
import { BASE_URL } from "./constants/general";
import { fetchAndCacheState } from "../Upshow";
import { getModel } from "../utils/models";
import { runReadinessLoading, surfaceReadinessResult, surfaceVisualTestUpNext, showTestStreamBanner, dismissTestStreamBanner } from "../states/readiness/bootOverlay";
import "./epg.scss";

const everpassLogo = `https://cdn.upshow.tv/ui-assets/EverPass_Horizontal_All_White.png`;

const BackButton = ({ goBack }) => {
    const { ref, focused } = useFocusable({
        focusKey: "BACK_BUTTON",
        onEnterPress: goBack,
    });

    return (
        <button ref={ref} className={`backBtn ${focused ? "focused" : ""}`} onClick={goBack} aria-label="Back" tabIndex={0}>
            <img src={backArrow} alt="Back" />
        </button>
    );
};

const TestStreamButton = ({ isSubscribed, onToggle, isLoading }) => {
    const { ref, focused } = useFocusable({
        focusKey: "TEST_STREAM_BUTTON",
        onEnterPress: onToggle,
    });
    return (
        <button ref={ref} className={`testStreamButton ${focused ? "isFocused" : ""}`} onClick={onToggle} disabled={isLoading} tabIndex={0}>
            <img src={streamingReadinessIcon} alt="" aria-hidden="true" className="testStreamButton__icon" />
            {isSubscribed ? "Stop Streaming Readiness" : "Readiness Test"}
        </button>
    );
};

const EPGHeader = ({ goBack, closeEPG, showMarketingButton, pause, resume }) => {
    const [currentTime, setCurrentTime] = useState(getNow());
    const [isSubscribedToTestStream, setIsSubscribedToTestStream] = useState(
        () => window.__UpshowState?.isSubscribedToTestStream ?? false
    );
    const [isTestStreamLoading, setIsTestStreamLoading] = useState(false);
    const { showFiltered } = useEvents();

    useClock(setCurrentTime);

    useEffect(() => {
        const handleStateUpdate = (event) => {
            setIsSubscribedToTestStream(event.detail?.isSubscribedToTestStream ?? false);
        };
        window.addEventListener("UpshowStateUpdate", handleStateUpdate);
        return () => window.removeEventListener("UpshowStateUpdate", handleStateUpdate);
    }, []);

    const handleTestStream = useCallback(async () => {
        if (isTestStreamLoading) return;
        setIsTestStreamLoading(true);

        if (isSubscribedToTestStream) {
            // Stop: unsubscribe then dismiss banner + close EPG on success.
            // Optimistic UI update so the button label changes immediately.
            setIsSubscribedToTestStream(false);
            if (window.__UpshowState) window.__UpshowState.isSubscribedToTestStream = false;
            try {
                await axios.post(`${BASE_URL}/v6/unsubscribe_test_stream`, {}, { headers: getHeaders() });
                dismissTestStreamBanner();
                fetchAndCacheState();
                closeEPG();
            } catch (err) {
                console.error("Test stream unsubscribe error:", err);
                // Roll back optimistic update; banner is still visible.
                setIsSubscribedToTestStream(true);
                if (window.__UpshowState) window.__UpshowState.isSubscribedToTestStream = true;
            } finally {
                setIsTestStreamLoading(false);
            }
            return;
        }

        // Subscribe flow: run checks, let the user decide, then start the stream.
        closeEPG();
        pause();
        try {
            const result = await runReadinessLoading();
            await surfaceReadinessResult(result, { dismissLabel: 'Continue' });
            const shouldRun = await surfaceVisualTestUpNext();
            if (shouldRun) {
                await axios.post(`${BASE_URL}/v6/subscribe_test_stream`, {}, { headers: getHeaders() });
                setIsSubscribedToTestStream(true);
                if (window.__UpshowState) window.__UpshowState.isSubscribedToTestStream = true;
                showTestStreamBanner();
                fetchAndCacheState();
            }
        } catch (err) {
            console.error('Test stream subscribe error:', err.message);
        } finally {
            resume();
            setIsTestStreamLoading(false);
        }
    }, [isTestStreamLoading, isSubscribedToTestStream, closeEPG, pause, resume]);

    const displayTime = useMemo(() => currentTime.format("hh:mm A"), [currentTime]);
    const model = getModel();
    const showTestStream = model.includes("chromeos") || model === "brightsign";

    const rightSection = useMemo(
        () => (
            <div className="rightButtons">
                {showMarketingButton && <MarketingButton closeEPG={closeEPG} />}
                {showTestStream && <TestStreamButton isSubscribed={isSubscribedToTestStream} onToggle={handleTestStream} isLoading={isTestStreamLoading} />}
                <h2>{displayTime}</h2>
            </div>
        ),
        [showMarketingButton, displayTime, showTestStream, isSubscribedToTestStream, handleTestStream, isTestStreamLoading, closeEPG]
    );

    return (
        <div className="headerInfo">
            {showFiltered ? <BackButton goBack={goBack} /> : <img src={everpassLogo} alt="EverPass" />}
            {rightSection}
        </div>
    );
};

export default EPGHeader;
